import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useProject } from '../contexts/ProjectContext';
import { LayoutDashboard, TrendingUp, Film, FileText, LogOut, ChevronLeft, ChevronRight, Share2, Upload, Menu, Settings, ChevronDown, Linkedin } from 'lucide-react';

const API_BASE = '/api';

export function Layout({ children, onLogout }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 1024);
  const [showSidebarOverlay, setShowSidebarOverlay] = useState(false);
  const [reelsReady, setReelsReady] = useState(false);
  const [linkedinReady, setLinkedinReady] = useState(false);
  const { currentProject, availableProjects, switchProject } = useProject();
  const [showProjectMenu, setShowProjectMenu] = useState(false);

  const isSmallScreen = () => window.innerWidth < 768;

  useEffect(() => {
    const handleResize = () => {
      if (isSmallScreen()) {
        setSidebarCollapsed(true);
      } else {
        setShowSidebarOverlay(false);
        setSidebarCollapsed(window.innerWidth < 1024);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check for reels and linkedin readiness periodically
  useEffect(() => {
    const checkStatus = async () => {
      // Always check for today's status
      const date = new Date().toISOString().split('T')[0];

      // Check Reels
      try {
        const response = await fetch(`${API_BASE}/reels-status.php?date=${date}`, { credentials: 'include' });
        const data = await response.json();

        if (data.ready) {
          setReelsReady(true);
          // Only show toast if it just became ready (optional logic, hard to track without prev state ref)
          // For now, let's skip toast on every poll if already ready, relying on badge.
          // But to be consistent with previous logic:
          if (!reelsReady) { 
             // Toast only once per session ideally, but for now simple check
          }
        }
      } catch (error) {
        console.error('Error checking reels status:', error);
      }

      // Check LinkedIn
      try {
        const response = await fetch(`${API_BASE}/linkedin-status.php?date=${date}`, { credentials: 'include' });
        const data = await response.json();

        if (data.ready) {
          setLinkedinReady(true);
        }
      } catch (error) {
        console.error('Error checking linkedin status:', error);
      }
    };

    // Check immediately on mount
    checkStatus();

    // Then check every 30 seconds
    const interval = setInterval(checkStatus, 30000);

    return () => clearInterval(interval);
  }, [reelsReady, linkedinReady]);

  const handleSwitchProject = async (projectId) => {
    const success = await switchProject(projectId);
    if (success) {
      toast.success(`Przełączono na: ${projectId}`);
      setShowProjectMenu(false);
      window.location.reload(); // Odświeżenie dla nowego contextu
    }
  };

  const currentProjectName = availableProjects.find(
    p => p.id === currentProject
  )?.name || 'Projekt';

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/preview', icon: FileText, label: 'Preview' },
    { path: '/social', icon: Share2, label: 'Social' },
    { path: '/trends', icon: TrendingUp, label: 'Trendy' },
    { path: '/reels', icon: Film, label: 'Reels', badge: reelsReady },
    { path: '/linkedin', icon: Linkedin, label: 'LinkedIn', badge: linkedinReady },
    { path: '/video', icon: Upload, label: 'Video' },
    { path: '/config', icon: Settings, label: 'Config' },
  ];

  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        {!sidebarCollapsed && (
          <h1 className="text-xl font-bold text-foreground">Newsletter Panel</h1>
        )}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-2 hover:bg-accent rounded-lg transition-colors hidden md:block"
        >
          {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => isSmallScreen() && setShowSidebarOverlay(false)}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-3 rounded-lg transition-colors mb-1 relative",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            )}
          >
            <item.icon size={20} />
            {!sidebarCollapsed && (
              <span className="font-medium flex items-center gap-2">
                {item.label}
                {item.badge && (
                  <span className="inline-flex items-center justify-center w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                )}
              </span>
            )}
            {sidebarCollapsed && item.badge && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="p-2 border-t">
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-3 py-3 rounded-lg w-full text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <LogOut size={20} />
          {!sidebarCollapsed && <span className="font-medium">Wyloguj</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Static sidebar for larger screens */}
      <aside className={cn(
        "bg-card border-r transition-all duration-300 flex-col hidden md:flex",
        sidebarCollapsed ? "w-16" : "w-64"
      )}>
        <SidebarContent />
      </aside>

      <div className="flex-1 flex flex-col">
        {/* Top header bar */}
        <header className="bg-card border-b p-4 flex items-center justify-between sticky top-0 z-30">
          {/* Mobile menu button */}
          <button onClick={() => setShowSidebarOverlay(true)} className="p-2 hover:bg-accent rounded-lg transition-colors md:hidden">
            <Menu size={24} />
          </button>

          {/* Project Switcher */}
          <div className="relative ml-auto">
            <button
              onClick={() => setShowProjectMenu(!showProjectMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <span className="font-medium">{currentProjectName}</span>
              <ChevronDown size={16} />
            </button>

            {showProjectMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white shadow-lg rounded-lg py-2 z-50">
                {availableProjects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => handleSwitchProject(project.id)}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
                      project.id === currentProject ? 'bg-blue-50 font-semibold' : ''
                    }`}
                  >
                    {project.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </header>

        {/* Mobile sidebar overlay */}
        {showSidebarOverlay && (
          <>
            <div 
              className="fixed inset-0 bg-black/60 z-40 md:hidden" 
              onClick={() => setShowSidebarOverlay(false)}
            ></div>
            <aside className={cn(
              "fixed top-0 left-0 h-full bg-card border-r transition-transform duration-300 flex flex-col z-50 w-64",
              showSidebarOverlay ? "translate-x-0" : "-translate-x-full"
            )}>
              <SidebarContent />
            </aside>
          </>
        )}

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
